import type {
  AdminCreateBrokerInput,
  AdminUpdateBrokerInput,
  BrokerPageContent,
  BrokerPageContentUpdateInput,
  BrokerProfileUpdateInput,
  BrokerWhatsappTemplatesUpdateInput,
  ClosedLostReason,
  CreatePropertyInput,
  CreateSaleInput,
  FunnelStage,
  LeadSource,
  LeadUpdateInput,
  ManualQualifyLeadInput,
  PresignedUploadRequest,
  PresignedUploadResponse,
  PriceRange,
  PropertyKind,
  PropertySpecs,
  PropertyStage,
  PropertyType,
  PropertyStatus,
  QualificationStatus,
  SubmitQualificationInput,
  SubmitQualificationResponse,
  UnitType,
  UpdatePropertyInput,
  WhatsappTemplates,
} from '@nexofacil/schemas';
import { getAuthHeader } from './auth-token.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/** Um problema de validação por campo, como o Zod descreve (path + mensagem). */
export type FieldIssue = { path: (string | number)[]; message: string };

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Presente quando a API recusou o corpo na validação (Zod). */
    public issues: FieldIssue[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Rótulos dos campos mais comuns, pra mensagem genérica de validação. Telas
// com formulário próprio (ex: imóvel) passam rótulos mais específicos.
const COMMON_FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  email: 'E-mail',
  phone: 'Telefone',
  whatsappNumber: 'WhatsApp',
  slug: 'Endereço da página',
  bio: 'Bio',
  description: 'Descrição',
  city: 'Cidade',
  state: 'Estado',
  neighborhood: 'Bairro',
};

/** Junta os problemas de validação numa frase que a pessoa consegue corrigir. */
export function describeIssues(
  issues: FieldIssue[],
  labelFor: (path: FieldIssue['path']) => string = (p) => COMMON_FIELD_LABELS[String(p[0])] ?? String(p[0]),
): string {
  const parts = issues.slice(0, 4).map((i) => `${labelFor(i.path)}: ${i.message}`);
  const extra = issues.length > 4 ? ` (e mais ${issues.length - 4})` : '';
  return `Confira ${issues.length === 1 ? 'este campo' : 'estes campos'}: ${parts.join(' · ')}${extra}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const auth = await getAuthHeader();
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...auth,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { message?: string; issues?: FieldIssue[] };
    };
    // Recusa do zValidator: o corpo traz o ZodError serializado (issues),
    // sem `message`. Antes isso virava "HTTP 400" sem dizer o que corrigir.
    const issues = Array.isArray(body.error?.issues) ? body.error.issues : [];
    const message =
      body.error?.message ?? (issues.length > 0 ? describeIssues(issues) : `HTTP ${res.status}`);
    throw new ApiError(res.status, message, issues);
  }
  return (await res.json()) as T;
}

// =============================================================================
// Public response shapes
// =============================================================================

export type PublicBrokerSummary = {
  id: string;
  subdomain: string;
  name: string;
  bio: string | null;
  avatarUrl: string | null;
  whatsappNumber: string;
  pageContent: BrokerPageContent;
  /**
   * Vertical the broker belongs to (e.g. 'mcmv-imobiliario', 'protecao-veicular').
   * Drives which landing content template the public page renders. Undefined
   * for legacy brokers without verticalId · FE falls back to MCMV.
   */
  verticalKey?: string;
  /** Pixel próprio do broker · carrega ADICIONAL ao Nexo pixel (não substitui). */
  metaPixelId?: string | null;
};

export type PublicPropertySummary = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  developer: string | null;
  city: string;
  state: string;
  neighborhood: string | null;
  priceRange: PriceRange | null;
  unitTypes: UnitType[];
  photos: string[];
  features: string[];
  stage: PropertyStage | null;
  kind: PropertyKind;
  propertyType: PropertyType | null;
  specs: PropertySpecs | null;
  referenceCode: string | null;
  featured: boolean;
};

export type PublicPropertyDetail = PublicPropertySummary & {
  description: string | null;
  location: { type: 'Point'; coordinates: [number, number] } | null;
};

export type GetBrokerResponse = {
  broker: PublicBrokerSummary;
  properties: PublicPropertySummary[];
};

export type GetPropertyResponse = {
  broker: Pick<PublicBrokerSummary, 'id' | 'subdomain' | 'name' | 'avatarUrl'> & {
    theme: BrokerPageContent['theme'] | null;
    avatarShape: BrokerPageContent['avatarShape'] | null;
    verticalKey?: string;
  };
  property: PublicPropertyDetail;
};

// =============================================================================
// Authenticated shapes (broker panel)
// =============================================================================

export type AuthBrokerProfile = {
  _id: string;
  firebaseUid: string;
  subdomain: string;
  /** Set once the broker is assigned to a vertical. Optional during multi-vertical rollout. */
  verticalId?: string;
  name: string;
  email: string;
  phone: string;
  whatsappNumber: string;
  bio?: string;
  avatarUrl?: string;
  status: 'active' | 'suspended';
  /** Override do content_category enviado no Meta CAPI · vazio = default da vertical. */
  metaContentCategory?: string;
  /** false = broker ainda não confirmou a senha padrão · modal de primeiro acesso aparece no painel. */
  hasChangedInitialPassword?: boolean;
  pageContent: BrokerPageContent;
  whatsappTemplates: WhatsappTemplates;
};

export type VerticalSummary = {
  _id: string;
  key: string;
  displayName: string;
  description?: string;
  brokerTermSingular: string;
  brokerTermPlural: string;
  listingTermSingular: string;
  listingTermPlural: string;
  isActive: boolean;
};

export type LeadListItem = {
  _id: string;
  brokerId: string;
  /** Vertical key snapshotted at submit. Permite a UI escolher renderer
   *  específico (AnswersGrid PV vs MCMV, badge neutro pra PV, etc). */
  verticalKey?: string;
  propertyId?: string;
  name: string;
  email: string;
  phone: string;
  qualificationStatus: QualificationStatus;
  manualQualificationOverride?: {
    reason: string;
    overriddenByFirebaseUid: string;
    overriddenByRole?: 'broker' | 'admin';
    overriddenByName?: string;
    overriddenAt: string;
  };
  funnelStage: FunnelStage;
  closedLostReason?: ClosedLostReason;
  source: LeadSource;
  notes?: string;
  lastContactedAt?: string;
  /** null = não abriu o WhatsApp da tela de resultado · ausente = lead anterior ao rastreio. */
  whatsappClickedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Contagens do corretor · calculadas no banco, sobre a base inteira. */
export type BrokerMetrics = {
  total: number;
  fully: number;
  partial: number;
  notQualified: number;
  fullyRate: number;
  today: number;
  last7Days: number;
  sales: number;
  /** Leads que chegaram na tela final e não abriram o WhatsApp. */
  notClickedWhatsapp: number;
};

/** Origem (utm_term curado) + quantos leads o corretor tem nela. */
export type LeadOriginCount = {
  /** utm_term, ou '__none__' quando o lead não tem campanha atribuída. */
  key: string;
  count: number;
};

export type QualificationSubmissionRecord = {
  _id: string;
  leadId: string;
  brokerId: string;
  verticalKey?: string;
  propertyId?: string;
  answers: Record<string, unknown>;
  computedQualification: {
    status: QualificationStatus;
    failedCriteria: string[];
    score: number;
    incomeCapCentsAtSubmission: number;
  };
  submittedAt: string;
};

export type PropertyAuth = {
  _id: string;
  brokerId: string;
  slug: string;
  name: string;
  tagline?: string;
  developer?: string;
  description?: string;
  city: string;
  state: string;
  neighborhood?: string;
  priceRange?: PriceRange | null;
  unitTypes: UnitType[];
  photos: string[];
  features: string[];
  stage?: PropertyStage | null;
  kind?: PropertyKind;
  propertyType?: PropertyType | null;
  specs?: PropertySpecs;
  referenceCode?: string;
  featured?: boolean;
  status: PropertyStatus;
  createdAt: string;
  updatedAt: string;
};

export type FunnelBreakdown = {
  submitted: number;
  contacted: number;
  visited: number;
  proposal: number;
  closed_won: number;
  closed_lost: number;
};

export type QualificationBreakdown = {
  not_qualified: number;
  partially_qualified: number;
  fully_qualified: number;
};

export type MetricsResponse = {
  windowDays: number;
  activeBrokers: number;
  totalLeads: number;
  totalQualified: number;
  totalFullyQualified: number;
  totalSales: number;
  qualificationRate: number;
  fullyQualifiedRate: number;
  previous: {
    totalLeads: number;
    totalQualified: number;
    totalFullyQualified: number;
    totalSales: number;
  };
  deltas: {
    totalLeads: number | null;
    totalQualified: number | null;
    totalFullyQualified: number | null;
    totalSales: number | null;
  };
  funnel: FunnelBreakdown;
  qualification: QualificationBreakdown;
  series: Array<{ date: string; count: number }>;
};

export type AdminLeadItem = LeadListItem & {
  broker: { _id: string; name?: string; subdomain?: string } | null;
  property: { _id: string; name?: string; slug?: string } | null;
};

export type AdminLeadDetailResponse = {
  lead: LeadListItem;
  submissions: QualificationSubmissionRecord[];
  broker: { _id: string; name: string; subdomain: string } | null;
  property: {
    _id: string;
    name: string;
    slug: string;
    city: string;
    state: string;
    neighborhood?: string;
  } | null;
};

export type BrokerPerformanceItem = {
  brokerId: string;
  name: string;
  subdomain: string;
  status: 'active' | 'suspended';
  /** Qualificacao binaria so faz sentido em MCMV · em PV todo lead e fully_qualified. */
  verticalKey?: string;
  totalLeads: number;
  qualified: number;
  fullyQualified: number;
  contacted: number;
  sales: number;
  qualificationRate: number;
  fullyQualifiedRate: number;
  contactRate: number;
  avgResponseMinutes: number | null;
  /** Leads rastreados (criados depois do rastreio de clique existir). */
  whatsappTracked: number;
  whatsappClicked: number;
  whatsappClickRate: number;
};

export type BreakdownEntry = {
  key: string;
  total: number;
  fullyQualified: number;
};

export type PropertyBreakdownEntry = {
  propertyId: string | null;
  name: string;
  total: number;
  fullyQualified: number;
};

export type BreakdownResponse = {
  windowDays: number;
  utm: {
    sources: BreakdownEntry[];
    mediums: BreakdownEntry[];
    campaigns: BreakdownEntry[];
    contents: BreakdownEntry[];
  };
  properties: PropertyBreakdownEntry[];
};

export type AdminLeadsListFilters = {
  status?: string;
  stage?: string;
  brokerId?: string;
  propertyId?: string;
  utmSource?: string;
  q?: string;
  days?: number;
  limit?: number;
  skip?: number;
};

// =============================================================================
// API client
// =============================================================================

export const api = {
  // Public
  getBroker: (subdomain: string) =>
    request<GetBrokerResponse>(`/public/broker/${encodeURIComponent(subdomain)}`),
  getProperty: (subdomain: string, slug: string) =>
    request<GetPropertyResponse>(
      `/public/broker/${encodeURIComponent(subdomain)}/property/${encodeURIComponent(slug)}`,
    ),
  submitQualification: (input: SubmitQualificationInput) =>
    request<SubmitQualificationResponse>('/public/submit-qualification', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /**
   * Fire-and-forget · o toque abre o WhatsApp e a página pode morrer logo em
   * seguida. `keepalive` mantém o request vivo e, sem corpo nem headers
   * custom, ele não dispara preflight de CORS.
   */
  trackWhatsappClick: (leadId: string) => {
    void fetch(`${API_URL}/public/leads/${encodeURIComponent(leadId)}/whatsapp-click`, {
      method: 'POST',
      keepalive: true,
    }).catch(() => {});
  },

  // Broker · profile + page
  getMyBrokerProfile: () =>
    request<{ broker: AuthBrokerProfile; vertical: VerticalSummary | null }>(
      '/broker/me',
    ),
  updateMyProfile: (input: BrokerProfileUpdateInput) =>
    request<{ ok: true }>('/broker/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  updateMyPageContent: (input: BrokerPageContentUpdateInput) =>
    request<{ ok: true; pageContent: BrokerPageContent }>('/broker/me/page-content', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  updateMyWhatsappTemplates: (input: BrokerWhatsappTemplatesUpdateInput) =>
    request<{ ok: true }>('/broker/me/whatsapp-templates', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  confirmPasswordKept: () =>
    request<{ ok: true }>('/broker/me/confirm-password-kept', {
      method: 'POST',
    }),

  // Broker · métricas
  getMyMetrics: () => request<BrokerMetrics>('/broker/metrics'),

  // Broker · leads
  listLeads: (params?: {
    status?: QualificationStatus;
    stage?: FunnelStage;
    /** utm_term curado, ou '__none__' pra leads sem campanha atribuída. */
    origin?: string;
    whatsapp?: 'clicked' | 'not_clicked';
    /** Busca por nome, e-mail ou telefone. */
    q?: string;
    limit?: number;
    skip?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.status) qs.set('status', params.status);
    if (params?.stage) qs.set('stage', params.stage);
    if (params?.origin) qs.set('origin', params.origin);
    if (params?.whatsapp) qs.set('whatsapp', params.whatsapp);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.skip) qs.set('skip', String(params.skip));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{
      leads: LeadListItem[];
      totalCount: number;
      origins: LeadOriginCount[];
    }>(`/broker/leads${suffix}`);
  },
  getLead: (id: string) =>
    request<{
      lead: LeadListItem;
      submissions: QualificationSubmissionRecord[];
      property: {
        _id: string;
        slug: string;
        name: string;
        city: string;
        state: string;
        neighborhood?: string;
      } | null;
    }>(`/broker/leads/${id}`),
  updateLead: (id: string, input: LeadUpdateInput) =>
    request<{ ok: true }>(`/broker/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  manualQualifyLead: (id: string, input: ManualQualifyLeadInput) =>
    request<{ ok: true }>(`/broker/leads/${id}/manual-qualify`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  createSale: (input: CreateSaleInput) =>
    request<{ sale: Record<string, unknown>; metaEventId: string; capiSuccess: boolean }>(
      '/broker/sales',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // Broker · properties
  listMyProperties: () => request<{ properties: PropertyAuth[] }>('/broker/properties'),
  getMyProperty: (id: string) =>
    request<{ property: PropertyAuth }>(`/broker/properties/${id}`),
  createMyProperty: (input: CreatePropertyInput) =>
    request<{ property: PropertyAuth }>('/broker/properties', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateMyProperty: (id: string, input: UpdatePropertyInput) =>
    request<{ ok: true }>(`/broker/properties/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  archiveMyProperty: (id: string) =>
    request<{ ok: true }>(`/broker/properties/${id}`, { method: 'DELETE' }),

  // Broker · uploads
  presignUpload: (input: PresignedUploadRequest) =>
    request<PresignedUploadResponse>('/broker/uploads/presigned-url', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // Admin
  adminListBrokers: () => request<{ brokers: AuthBrokerProfile[] }>('/admin/brokers'),
  adminGetBroker: (id: string) =>
    request<{ broker: AuthBrokerProfile }>(`/admin/brokers/${id}`),
  adminCreateBroker: (input: AdminCreateBrokerInput) =>
    request<{ broker: AuthBrokerProfile }>('/admin/brokers', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  adminUpdateBroker: (id: string, input: AdminUpdateBrokerInput) =>
    request<{ ok: true }>(`/admin/brokers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  adminMetrics: (days?: number) =>
    request<MetricsResponse>(`/admin/metrics${days ? `?days=${days}` : ''}`),
  adminListLeads: (filters: AdminLeadsListFilters = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    }
    const qs = params.toString();
    return request<{
      leads: AdminLeadItem[];
      totalCount: number;
      windowDays: number;
    }>(`/admin/leads${qs ? `?${qs}` : ''}`);
  },
  adminGetLead: (id: string) =>
    request<AdminLeadDetailResponse>(`/admin/leads/${id}`),
  adminBrokerPerformance: (days?: number) =>
    request<{ windowDays: number; brokers: BrokerPerformanceItem[] }>(
      `/admin/brokers/performance${days ? `?days=${days}` : ''}`,
    ),
  adminBreakdown: (days?: number) =>
    request<BreakdownResponse>(
      `/admin/breakdown${days ? `?days=${days}` : ''}`,
    ),
  adminManualQualifyLead: (id: string, input: ManualQualifyLeadInput) =>
    request<{ ok: true }>(`/admin/leads/${id}/manual-qualify`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  adminCreateSale: (leadId: string, input: Omit<CreateSaleInput, 'leadId'>) =>
    request<{ sale: Record<string, unknown>; metaEventId: string; capiSuccess: boolean }>(
      `/admin/leads/${leadId}/sale`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
};
